// Frontend/src/api/buildings.ts
import api from "./client";

export type PaymentConfigStatus = "CONFIGURED" | "HOA_DEFAULT" | "MISSING";

export type Building = {
  id: string;
  code: string;
  name: string;
  address: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  total_units: number;
  floors: number;
  default_monthly_fee: string; // decimal as string from backend
  is_active: boolean;
  notes: string;
  rif: string;
  rif_image: string | null;
  unit_count: number;
  resident_count: number;
  payment_config_status?: PaymentConfigStatus;
  has_building_payment_config?: boolean;
  created_at: string;
  updated_at: string;
};

export type BuildingList = {
  id: string;
  code: string;
  name: string;
  address: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  total_units: number;
  floors: number;
  is_active: boolean;
  rif: string;
  rif_image: string | null;
  unit_count: number;
  resident_count: number;
  payment_config_status?: PaymentConfigStatus;
  has_building_payment_config?: boolean;
  created_at: string;
  updated_at: string;
};

export type BuildingPaymentConfig = {
  id: string;
  building: string;
  building_name: string;
  building_code: string;
  bank_name?: string;
  bank_account_number?: string;
  bank_routing_number?: string;
  account_holder_name?: string;
  zelle_email?: string;
  zelle_phone?: string;
  paypal_email?: string;
  venmo_username?: string;
  cashapp_username?: string;
  pago_movil_bank?: string;
  pago_movil_phone?: string;
  pago_movil_id_number?: string;
  cash_instructions?: string;
  is_verified: boolean;
  is_active: boolean;
  migrated_from_user_default: boolean;
  created_at: string;
  updated_at: string;
};

export type BuildingPaymentConfigResponse = {
  building_id: string;
  building_name: string;
  building_code: string;
  payment_config_status: PaymentConfigStatus;
  has_building_payment_config: boolean;
  config: BuildingPaymentConfig | null;
  fallback_payment_details: Record<string, any> | null;
};

export type UpdateBuildingPaymentConfigData = Partial<Omit<
  BuildingPaymentConfig,
  | "id"
  | "building"
  | "building_name"
  | "building_code"
  | "migrated_from_user_default"
  | "created_at"
  | "updated_at"
>>;

export type CreateBuildingData = {
  code: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  total_units?: number;
  floors?: number;
  default_monthly_fee?: number;
  is_active?: boolean;
  notes?: string;
  rif?: string;
};

export type UpdateBuildingData = Partial<CreateBuildingData>;

export type BuildingStats = {
  building_id: string;
  name: string;
  code: string;
  total_units: number;
  occupied_units: number;
  vacant_units: number;
  inactive_units: number;
  total_residents: number;
  primary_residents: number;
  secondary_residents: number;
  total_balance: string;
  average_monthly_fee: string;
  occupancy_rate: number;
};

export type CSVImportResult = {
  success: boolean;
  total_processed: number;
  success_count: number;
  error_count: number;
  errors: Array<{
    row: number;
    errors: string[];
  }>;
  created_building_ids?: string[];
  created_unit_ids?: string[];
  created_resident_ids?: string[];
};

/**
 * List all buildings
 */
export async function listBuildings(): Promise<BuildingList[]> {
  const res = await api.get<BuildingList[]>("/buildings/");
  return res.data;
}

/**
 * Get a single building by ID
 */
export async function getBuilding(id: string): Promise<Building> {
  const res = await api.get<Building>(`/buildings/${id}/`);
  return res.data;
}

/**
 * Build FormData for building create/update when a rif_image file is included.
 */
function toBuildingPayload(data: CreateBuildingData | UpdateBuildingData, rifImageFile?: File | null): FormData | CreateBuildingData | UpdateBuildingData {
  if (!rifImageFile) return data;
  const fd = new FormData();
  Object.entries(data).forEach(([k, v]) => {
    if (v !== undefined && v !== null) fd.append(k, String(v));
  });
  fd.append("rif_image", rifImageFile);
  return fd;
}

/**
 * Create a new building
 */
export async function createBuilding(data: CreateBuildingData, rifImageFile?: File | null): Promise<Building> {
  const payload = toBuildingPayload(data, rifImageFile);
  const res = await api.post<Building>("/buildings/", payload);
  return res.data;
}

/**
 * Update an existing building
 */
export async function updateBuilding(id: string, data: UpdateBuildingData, rifImageFile?: File | null): Promise<Building> {
  const payload = toBuildingPayload(data, rifImageFile);
  const res = await api.patch<Building>(`/buildings/${id}/`, payload);
  return res.data;
}

/**
 * Delete a building
 */
export async function deleteBuilding(id: string): Promise<void> {
  await api.delete(`/buildings/${id}/`);
}

/**
 * Get statistics for a building
 */
export async function getBuildingStats(id: string): Promise<BuildingStats> {
  const res = await api.get<BuildingStats>(`/buildings/${id}/stats/`);
  return res.data;
}

/**
 * Get building payment configuration and fallback status.
 */
export async function getBuildingPaymentConfig(id: string): Promise<BuildingPaymentConfigResponse> {
  const res = await api.get<BuildingPaymentConfigResponse>(`/buildings/${id}/payment-config/`);
  return res.data;
}

/**
 * Create or update building payment configuration.
 */
export async function updateBuildingPaymentConfig(
  id: string,
  data: UpdateBuildingPaymentConfigData
): Promise<BuildingPaymentConfig> {
  const res = await api.patch<BuildingPaymentConfig>(`/buildings/${id}/payment-config/`, data);
  return res.data;
}

/**
 * Import buildings from CSV file
 */
export async function importBuildingsCSV(file: File): Promise<CSVImportResult> {
  const formData = new FormData();
  formData.append("file", file);
  
  const res = await api.post<CSVImportResult>("/buildings/import-csv/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return res.data;
}

/**
 * Import units for a specific building from CSV
 */
export async function importBuildingUnitsCSV(buildingId: string, file: File): Promise<CSVImportResult> {
  const formData = new FormData();
  formData.append("file", file);
  
  const res = await api.post<CSVImportResult>(`/buildings/${buildingId}/units/import-csv/`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return res.data;
}

/**
 * Import residents for a specific building from CSV
 */
export async function importBuildingResidentsCSV(buildingId: string, file: File): Promise<CSVImportResult> {
  const formData = new FormData();
  formData.append("file", file);
  
  const res = await api.post<CSVImportResult>(`/buildings/${buildingId}/residents/import-csv/`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return res.data;
}

/**
 * Download buildings CSV template
 */
export async function downloadBuildingsTemplate(): Promise<Blob> {
  const res = await api.get("/buildings/template/", {
    responseType: "blob",
  });
  return res.data;
}
